<template>
  <v-app>
    <v-content>
      <v-container>
        <v-row align="center" justify="center">
          <v-col cols="12" md="4" sm="8">
            <!-- LOGIN FORM -->
            <v-form
              @submit.prevent="logUserIn"
              id="loginForm"
              ref="loginForm"
              v-if="form === 'loginForm'"
            >
              <v-card class="elevation-12" :loading="loading">
                <v-toolbar color="info lighten-1" dark flat>
                  <v-toolbar-title>Admin login</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <v-text-field
                    prepend-icon="mdi-account"
                    :rules="emailRules"
                    label="Email"
                    name="email"
                    type="text"
                    v-model="email"
                    autofocus
                  />

                  <v-text-field
                    prepend-icon="mdi-lock"
                    :rules="passwordRules"
                    id="password"
                    label="Password"
                    name="password"
                    type="password"
                    v-model="password"
                  />
                  <p v-if="error" class="error--text text-right">{{ error }}</p>
                  <p class="text-right">
                    <a @click.stop="form = 'resetPassword'">Forgot password?</a>
                  </p>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" type="submit">login</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>

            <!-- PASSWORD RESET REQUIRED FORM -->
            <v-form
              @submit.prevent="completeNewPasswordRequired"
              id="password-reset-required"
              ref="newPassword"
              v-else-if="form === 'newPassword'"
            >
              <v-card class="elevation-12">
                <v-toolbar color="info lighten-1" dark flat>
                  <v-toolbar-title>Password reset required</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <v-text-field
                    prepend-icon="mdi-lock"
                    :rules="passwordRules"
                    id="password"
                    label="New password"
                    name="password"
                    type="password"
                    v-model="password"
                    autofocus
                  />
                  <p v-if="error" class="error--text text-right">{{ error }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" type="submit">Reset Password</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>

            <!-- MFA FORM -->
            <v-form
              @submit.prevent="completeMFA"
              id="mfa-required"
              ref="mfa"
              v-else-if="form === 'mfa'"
            >
              <v-card class="elevation-12" :loading="loading">
                <v-toolbar color="info lighten-1" dark flat>
                  <v-toolbar-title>MFA Required</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <p>A code will be sent to you via SMS.</p>
                  <v-text-field
                    prepend-icon="mdi-shield-lock"
                    :rules="mfaRules"
                    id="mfa"
                    label="MFA code"
                    name="mfa"
                    type="text"
                    v-model="mfaCode"
                    autofocus
                  />
                  <p v-if="error" class="error--text text-right">{{ error }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" type="submit">submit</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>

            <!-- FORGOT PASSWORD FORM -->
            <v-form
              @submit.prevent="requestNewPassword"
              id="forgot-password"
              ref="requestPasswordForm"
              v-else-if="form === 'resetPassword'"
            >
              <v-card class="elevation-12">
                <v-toolbar color="info lighten-1" dark flat>
                  <v-toolbar-title>Request password reset</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <v-text-field
                    prepend-icon="mdi-email"
                    :rules="emailRules"
                    id="email"
                    label="Email"
                    name="email"
                    required
                    type="text"
                    v-model="email"
                    autofocus
                  />
                  <p v-if="error" class="error--text text-right">{{ error }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" type="submit"
                    >Request New Password</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-form>

            <!-- PASSWORD RESET FORM -->
            <v-form
              @submit.prevent="confirmPassword"
              id="password-code"
              ref="passwordCode"
              v-else-if="form === 'passwordCode'"
            >
              <v-card class="elevation-12">
                <v-toolbar color="info lighten-1" dark flat>
                  <v-toolbar-title>Admin login</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <v-text-field
                    prepend-icon="mdi-shield-lock"
                    id="password-code"
                    label="Password reset code from email"
                    name="password-code"
                    required
                    type="text"
                    v-model="passwordResetCode"
                    autofocus
                  />

                  <v-text-field
                    prepend-icon="mdi-lock"
                    :rules="passwordRules"
                    id="password"
                    label="New password"
                    name="password"
                    type="password"
                    v-model="password"
                  />
                  <p v-if="error" class="error--text text-right">{{ error }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="primary" type="submit">submit</v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "Index",
  title: "Login",
  data() {
    return {
      form: "loginForm",
      email: null,
      password: null,
      mfaCode: null,
      passwordResetCode: null,
      cognitoResponse: null,
      cognitoUser: null,
      error: null,
      loading: false,

      // FORM RULES
      emailRules: [v => !!v || "Email is required"],
      passwordRules: [v => !!v || "Password is required"],
      mfaRules: [v => !!v || "MFA code is required"]
    };
  },

  watch: {
    /** RESET FORM VALIDATION AND FOCUS ON FIRST FIELD IN FORM */
    form(newVal, oldVal) {
      if (newVal === oldVal) return;
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs[newVal]) {
            this.$refs[newVal].resetValidation();
            this.$refs[newVal].$el[0].focus();
          }
        });
      }
    }
  },

  methods: {
    ...mapActions({
      login: "auth/login",
      cognitoCompleteNewPasswordRequired:
        "cognito/cognitoCompleteNewPasswordRequired",
      cognitoCompleteMFA: "cognito/cognitoCompleteMFA",
      cognitoConfirmPassword: "cognito/cognitoConfirmPassword",
      cognitoRequestPasswordReset: "cognito/cognitoRequestPasswordReset",
      symfonyLogin: "symfonyUser/symfonyLogin"
    }),

    async logUserIn(event) {
      console.log("STARTED LOGIN USER FUNCTION", event);
      if (event) {
        event.preventDefault();
      }

      this.error = null;
      try {
        this.loading = true;
        await this.login({
          username: this.email.toLowerCase(),
          password: this.password
        });
        this.loading = false;

        await this.$router.push({ name: "CompaniesList" });
      } catch (e) {
        this.loading = false;
        console.error("ERROR LOGGING IN", e);

        // PASSWORD RESET REQUIRED
        if (e.code && e.code === "PasswordResetRequiredException") {
          this.form = "passwordCode";
          this.password = null;
          return;
        }

        if (e.message) {
          this.error = e.message;
          return;
        }
        this.error =
          "Error logging in. Contact the tech team if this error continues to happen.";
      }
    },

    async completeNewPasswordRequired() {
      try {
        this.loading = true;
        const user = await this.cognitoCompleteNewPasswordRequired(
          this.password
        );
        console.log("USER", user);

        const symfonyUser = await this.symfonyLogin({
          email: this.email,
          password: this.password
        });
        console.log("the symfony login user obj", symfonyUser);

        // REDIRECT
        this.$router.push({ name: "CompaniesList" });
      } catch (e) {
        console.error("ERROR SETTING NEW PASSWORD", e);
        this.loading = false;
        this.error = e.message;
      }
    },

    async completeMFA() {
      this.error = null;
      try {
        this.loading = true;
        await this.cognitoCompleteMFA(this.mfaCode);

        const symfonyUser = await this.symfonyLogin({
          email: this.email,
          password: this.password
        });
        console.log("the symfony login user obj", symfonyUser);

        this.$router.push({ name: "CompaniesList" });
      } catch (e) {
        this.loading = false;
        console.error(e);
        if (e.code && e.code === "CodeMismatchException") {
          this.error = "Invalid code";
          return;
        }
        this.error = e.message;
      }
    },

    async confirmPassword() {
      try {
        this.loading = true;
        await this.cognitoConfirmPassword({
          newPassword: this.password,
          verificationCode: this.passwordResetCode,
          username: this.email
        });
        this.form = "loginForm";
        this.loading = false;
      } catch (e) {
        this.loading = false;
        console.error("ERROR CONFIRMING PASSWORD", e);
        if (e.code && e.code === "ExpiredCodeException") {
          this.error = e.message;
        } else if (e.code && e.code === "CodeMismatchException") {
          this.error = e.message;
        }
      }
    },

    async requestNewPassword() {
      try {
        this.loading = true;
        const valid = this.$refs.requestPasswordForm.validate();
        if (!valid) {
          return;
        }
        await this.cognitoRequestPasswordReset(this.email);
        this.form = "passwordCode";
        this.password = null;
        this.loading = false;
      } catch (e) {
        this.loading = false;
        console.error("ERROR REQUESTING NEW PASSWORD", e);
      }
    }
  }
};
</script>
